import React, { useState, useCallback, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import axios from 'axios';
import { displayDate } from "../../utils/dateUtils";
import { canToggleEntityStatus, getEntityStatusToggleTooltip } from "../../utils/statusToggle";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Tooltip,
  Snackbar,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search as SearchIcon,
  Visibility,
  Close,
  Download,
  Upload,
  School,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  FileDownload,
  AccessTime,
} from "@mui/icons-material";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useQueryClient } from '@tanstack/react-query';
import courseService from "../../services/courseService";
import classService from "../../services/classService";
import api from "../../services/api";
import { DataGrid } from "@mui/x-data-grid";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import DepartmentModuleFilterSelect from "../../components/common/DepartmentModuleFilterSelect";
import { useDepartmentModuleFilter } from "../../hooks/useDepartmentModuleFilter";
import StatusDropdown from "../../components/common/StatusDropdown";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  InputAdornment,
  Switch
} from '@mui/material';

import { resolveUserDisplayName, resolveUserEmail } from "../../utils/userDisplay";

function getClassTeacherRow(row) {
  return row?.classTeacher || row?.teacher || row?.Teacher || null;
}

function getClassTeacherDisplayName(row) {
  const t = getClassTeacherRow(row);
  if (!t) return "—";
  const u = t.user || t.User;
  return resolveUserDisplayName(u || t, { fallback: "—" });
}

function getClassTeacherEmail(row) {
  const t = getClassTeacherRow(row);
  if (!t) return "";
  const u = t.user || t.User;
  return resolveUserEmail(u || t);
}

const Classes = () => {
  const queryClient = useQueryClient();
  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const {
    departmentFilter,
    setDepartmentFilter,
    showAllDepartmentsOption,
    effectiveInstituteId,
  } = useDepartmentModuleFilter();
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  
  // Search and Pagination State
  const [searchQuery, setSearchQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([
    {
      field: 'name',
      sort: 'desc',
    },
  ]);
  
  // Define all state at the top level
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClassId, setEditingClassId] = useState(null);
  const [viewingClass, setViewingClass] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [classToDelete, setClassToDelete] = useState(null);
  const [importStep, setImportStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [selectedImportReport, setSelectedImportReport] = useState(null);
  const [createClassDialogOpen, setCreateClassDialogOpen] = useState(false);
  const [viewClassDialogOpen, setViewClassDialogOpen] = useState(false);
  const [deleteClassDialogOpen, setDeleteClassDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResultsDialogOpen, setImportResultsDialogOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classFormData, setClassFormData] = useState({
    name: "",
    code: "",
    duration: "",
    description: "",
    departmentId: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openAddClassDialog, setOpenAddClassDialog] = useState(false);
  const [classForm, setClassForm] = useState({
    name: '',
    section: '',
    courseId: '',
    teacherId: '',
    practicalBatchesInput: '',
    capacity: ''
  });
  const [teachers, setTeachers] = useState([]);
  /** When editing, ensure the row's course appears in the dropdown even if the list query is stale or filtered. */
  const [editCourseFallback, setEditCourseFallback] = useState(null);
  /** When editing, names API may omit other departments (e.g. HOD-scoped list); merge row department so Select matches. */
  const [editDepartmentFallback, setEditDepartmentFallback] = useState(null);
  const [loading, setLoading] = useState({
    teachers: false,
    submitting: false
  });

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [departmentFilter, statusFilter, courseFilter, searchQuery]);
  
  // Define all mutations at the top level
  const toggleClassStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await api.put(`/class/class-status-update?id=${id}`, { status });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch classes queries reliably so UI reflects status change
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.refetchQueries({ queryKey: ['classes'] });
      setSnackbar({
        open: true,
        message: 'Class status updated successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update class status',
        severity: 'error'
      });
    }
  });

  const handleStatusToggle = (row) => {
    const id = row?.id;
    const currentStatus = row?.status;
    if (id == null) return;
    if (currentStatus && row?.canDeactivate === false) return;
    toggleClassStatus.mutate({ id, status: !currentStatus });
  };

  const handleViewClass = (classData) => {
    console.log('Viewing class:', classData);
    setViewingClass({
      ...classData,
      department: classData.Department || classData.department || {},
      course: classData.Course || classData.course || {},
      classTeacher: classData.classTeacher || classData.teacher || classData.Teacher || null,
    });
    setViewClassDialogOpen(true);
  };

  const handleEditClass = (classData) => {
    console.log('Editing class data:', classData);
    setIsEditMode(true);
    setEditingClassId(classData.id);

    const courseObj = classData.Course || classData.course;
    const teacherRow = classData.classTeacher || classData.teacher || classData.Teacher;
    const courseIdRaw =
      classData.courseId != null && classData.courseId !== ""
        ? classData.courseId
        : courseObj?.id ?? "";
    const teacherIdRaw =
      classData.teacherId != null && classData.teacherId !== ""
        ? classData.teacherId
        : teacherRow?.id ?? "";
    const departmentIdRaw =
      classData.departmentId != null && classData.departmentId !== ""
        ? classData.departmentId
        : classData.department?.id ?? "";

    // Handle both form structures
    if (classData.section !== undefined) {
      if (courseObj?.id != null) {
        setEditCourseFallback({
          id: courseObj.id,
          name: courseObj.name,
          code: courseObj.code,
        });
      } else {
        setEditCourseFallback(null);
      }

      const deptObj = classData.department || classData.Department;
      if (deptObj?.id != null) {
        setEditDepartmentFallback({
          id: deptObj.id,
          name: deptObj.name || `Department ${deptObj.id}`,
        });
      } else if (departmentIdRaw !== "" && departmentIdRaw != null) {
        setEditDepartmentFallback({
          id: departmentIdRaw,
          name: `Department #${departmentIdRaw}`,
        });
      } else {
        setEditDepartmentFallback(null);
      }

      // String IDs so MUI Select values match MenuItem values (strict equality)
      setClassForm({
        id: classData.id,
        name: classData.name || "",
        section: classData.section || "",
        courseId: courseIdRaw !== "" && courseIdRaw != null ? String(courseIdRaw) : "",
        teacherId: teacherIdRaw !== "" && teacherIdRaw != null ? String(teacherIdRaw) : "",
        practicalBatchesInput: Array.isArray(classData.practicalBatches) ? classData.practicalBatches.join(', ') : '',
        duration: classData.duration || "",
        description: classData.description || "",
        departmentId:
          departmentIdRaw !== "" && departmentIdRaw != null ? String(departmentIdRaw) : "",
        status: classData.status !== undefined ? classData.status : true,
        capacity: classData.capacity != null && classData.capacity !== "" ? String(classData.capacity) : "",
      });

      setOpenAddClassDialog(true);

      const cid = courseIdRaw !== "" && courseIdRaw != null ? String(courseIdRaw) : "";
      if (cid) {
        // Edit flow: pass classId so the backend preferred/homeroom override keeps the
        // existing class teacher resolvable even if they belong to another department.
        fetchTeachers(cid, { classId: classData?.id }).then((list) => {
          const arr = Array.isArray(list) ? list : [];
          const tid =
            teacherIdRaw !== "" && teacherIdRaw != null ? String(teacherIdRaw) : "";
          if (!tid) return;
          const inList = arr.some((t) => String(t.id) === tid);
          if (!inList && teacherRow?.id != null) {
            const u = teacherRow.user || teacherRow.User;
            setTeachers([
              ...arr,
              {
                id: teacherRow.id,
                fullName: u?.fullName || teacherRow.fullName || `Teacher ${teacherRow.id}`,
                email: u?.email,
              },
            ]);
          }
        });
      }
    } else {
      setEditCourseFallback(null);
      setEditDepartmentFallback(null);
      // For the detailed class form
      setClassFormData({
        id: classData.id,
        name: classData.name || "",
        code: classData.code || "",
        duration: classData.duration ? String(classData.duration) : "",
        description: classData.description || "",
        departmentId: classData.departmentId || (classData.department ? classData.department.id : ""),
        status: classData.status || false
      });
      setCreateClassDialogOpen(true);
    }
    
    void refetchCourses();
  };

  const handleDeleteClick = (classData) => {
    setClassToDelete(classData);
    setDeleteClassDialogOpen(true);
  };

  // Fetch courses using React Query
  const { 
    data: coursesResponse, 
    isLoading: isLoadingCourses, 
    isError: isCoursesError,
    error: coursesError,
    refetch: refetchCourses
  } = useQuery({
    queryKey: [
      'courses',
      String(openAddClassDialog ? (classForm.departmentId || "") : (departmentFilter || "")),
    ],
    queryFn: ({ queryKey }) => {
      const dep = (queryKey?.[1] || "").trim();
      return dep ? courseService.getCoursesByDepartment(dep) : courseService.getCourseNames();
    },
    enabled: true, // fetch on mount for filter
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    onError: (error) => {
      console.error('Error in courses query:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to load courses. Please try again.',
        severity: 'error'
      });
    }
  });
  
  // Log when the component mounts and when courses are fetched
  useEffect(() => {
    console.log('Component mounted, courses state:', { 
      isLoading: isLoadingCourses, 
      isError: isCoursesError,
      data: coursesResponse,
      error: coursesError
    });
  }, [isLoadingCourses, isCoursesError, coursesResponse, coursesError]);

  // Ensure courses is always an array
  const courses = React.useMemo(() => {
    if (!coursesResponse) return [];
    
    console.log('Raw courses response:', coursesResponse);
    
    // Normalize axios responses and API envelopes:
    // - course-names: { status, message, data: [...] }
    // - assignment/courses-names: { status, message, data: [...] }
    // - direct arrays in some legacy places
    const envelope = coursesResponse?.data ?? coursesResponse;
    const maybeArray =
      Array.isArray(envelope) ? envelope :
      Array.isArray(envelope?.data) ? envelope.data :
      Array.isArray(envelope?.data?.data) ? envelope.data.data :
      Array.isArray(envelope?.rows) ? envelope.rows :
      [];
    return maybeArray
      .filter((c) => c && (c.id != null || c._id != null))
      .filter((c) => String(c.name ?? c.courseName ?? c.title ?? "").trim() !== "");
  }, [coursesResponse]);

  const coursesForSelect = React.useMemo(() => {
    const normalized = (courses || [])
      .map((c) => ({
        id: c.id ?? c._id ?? c.courseId,
        name: c.name ?? c.courseName ?? c.title ?? (c.id != null ? `Course ${c.id}` : "Course"),
      }))
      .filter((c) => c.id != null && String(c.name || "").trim() !== "");

    const out = [...normalized];
    if (editCourseFallback?.id != null) {
      const fid = String(editCourseFallback.id);
      if (!out.some((c) => String(c.id) === fid)) {
        out.unshift({
          id: editCourseFallback.id,
          name:
            editCourseFallback.name ||
            editCourseFallback.code ||
            `Course ${editCourseFallback.id}`,
        });
      }
    }
    return out;
  }, [courses, editCourseFallback]);

  // Log course loading states for debugging
  useEffect(() => {
    console.log('Courses state:', { 
      isLoading: isLoadingCourses, 
      isError: isCoursesError, 
      data: coursesResponse,
      courses: courses,
      error: coursesError
    });
  }, [isLoadingCourses, isCoursesError, coursesResponse, courses, coursesError]);
  
  // Open add class dialog and fetch courses
  const handleOpenAddClass = () => {
    console.log('Opening add class dialog');
    
    // Reset all form states
    setIsEditMode(false);
    setEditingClassId(null);
    setClassForm({
      name: '',
      section: '',
      courseId: '',
      teacherId: '',
      practicalBatchesInput: '',
      duration: '',
      description: '',
      departmentId: '',
      status: true,
      capacity: ''
    });
    setTeachers([]);
    setEditCourseFallback(null);
    setEditDepartmentFallback(null);

    setOpenAddClassDialog(true);
    
    // Manually trigger the courses fetch
    console.log('Triggering courses fetch...');
    refetchCourses()
      .then(response => {
        console.log('Courses fetch successful:', response);
      })
      .catch(error => {
        console.error('Failed to fetch courses:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load courses. Please try again.',
          severity: 'error'
        });
      });
  };

  // Fetch teachers based on selected course
  const fetchTeachers = useCallback(async (courseId, opts = {}) => {
    if (!courseId) {
      setTeachers([]);
      return [];
    }

    // Scope teachers to the selected department (and course). classId is passed only in the
    // edit flow so the backend keeps the existing/homeroom class teacher resolvable.
    const { departmentId, classId } = opts || {};
    const params = { id: courseId };
    if (departmentId != null && String(departmentId).trim() !== "") {
      params.departmentId = String(departmentId).trim();
    }
    if (classId != null && String(classId).trim() !== "") {
      params.classId = String(classId).trim();
    }

    console.log('Fetching teachers for course ID:', courseId, 'params:', params);
    setLoading(prev => ({ ...prev, teachers: true }));

    try {
      const response = await api.get(`/course/course-teachers`, {
        params
      });

      console.log('Teachers API Full Response:', response);

      if (response.data && response.data.status === 200 && Array.isArray(response.data.data)) {
        console.log('Setting teachers data:', response.data.data);
        setTeachers(response.data.data);
        return response.data.data;
      }
      console.warn('Unexpected teachers data format. Response data:', response.data);
      setTeachers([]);
      setSnackbar({
        open: true,
        message: response.data?.message || 'Unexpected response format from server',
        severity: 'error'
      });
      return [];
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to load teachers. Please try again.',
        severity: 'error'
      });
      return [];
    } finally {
      setLoading(prev => ({ ...prev, teachers: false }));
    }
  }, []);

  // Handle course change
  const handleCourseChange = (e) => {
    const courseId =
      e.target.value != null && e.target.value !== "" ? String(e.target.value) : "";
    console.log('Course selected, fetching teachers for course ID:', courseId);

    // Reset form and fetch teachers for the selected course
    setClassForm(prev => ({
      ...prev,
      courseId,
      teacherId: '' // Reset teacher when course changes
    }));

    // Reset teachers list when changing courses
    setTeachers([]);

    // Only fetch teachers if a valid course is selected.
    // Scope to the currently selected department so only that department's teachers appear.
    if (courseId) {
      fetchTeachers(courseId, { departmentId: classForm.departmentId });
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "departmentId") {
      setEditDepartmentFallback(null);
      setClassForm((prev) => ({
        ...prev,
        departmentId: value,
        courseId: "",
        teacherId: "",
      }));
      setTeachers([]);
      return;
    }
    setClassForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Block number keys in section (letters only, exactly 1 character)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleSectionChange = (e) => {
    const value = e.target.value.replace(/\d/g, '').slice(0, 1);
    setClassForm(prev => ({ ...prev, section: value }));
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };


  // Close add class dialog
  const handleCloseAddClass = () => {
    setOpenAddClassDialog(false);
    setIsEditMode(false);
    setEditingClassId(null);
    setClassForm({
      name: '',
      section: '',
      courseId: '',
      teacherId: '',
      practicalBatchesInput: '',
      duration: '',
      description: '',
      departmentId: '',
      status: true
    });
    setTeachers([]);
    setEditCourseFallback(null);
    setEditDepartmentFallback(null);
  };

  // Validation for Add/Edit Class form using classForm state
  const validateAddClassForm = () => {
    const errors = {};
    const nameTrimmed = (classForm.name || '').trim();
    if (!nameTrimmed) {
      errors.name = 'Class name is required';
    } else if (nameTrimmed.length < 2) {
      errors.name = 'Class name must be at least 2 characters long';
    } else if (nameTrimmed.length > 30) {
      errors.name = 'Class name cannot exceed 30 characters';
    }

    const sectionTrimmed = (classForm.section || '').trim();
    if (!sectionTrimmed) {
      errors.section = 'Section is required';
    } else if (sectionTrimmed.length !== 1) {
      errors.section = 'Section must be exactly 1 character';
    }

    // Department is required
    if (!classForm.departmentId) {
      errors.department = 'Please select a department';
    }

    // Course is required
    if (!classForm.courseId) {
      errors.course = 'Please select a course';
    }

    // Teacher is required
    if (!classForm.teacherId) {
      errors.teacher = 'Please select a teacher';
    }

    return errors;
  };

  // Validation function for class form
  const validateForm = () => {
    const errors = {};
    
    // Validate class name
    if (!classFormData.name || !classFormData.name.trim()) {
      errors.name = 'Class name is required';
    } else if (classFormData.name.length < 2) {
      errors.name = 'Class name must be at least 2 characters long';
    } else if (classFormData.name.length > 50) {
      errors.name = 'Class name cannot exceed 50 characters';
    }
    
    // Validate section
    if (!classFormData.section || !classFormData.section.trim()) {
      errors.section = 'Section is required';
    } else if (classFormData.section.length > 10) {
      errors.section = 'Section cannot exceed 10 characters';
    }
    
    // Validate course
    if (!classFormData.courseId) {
      errors.course = 'Please select a course';
    } else if (isNaN(parseInt(classFormData.courseId, 10))) {
      errors.course = 'Invalid course selection';
    }
    
    // Validate teacher
    if (!classFormData.teacherId) {
      errors.teacher = 'Please select a teacher';
    } else if (isNaN(parseInt(classFormData.teacherId, 10))) {
      errors.teacher = 'Invalid teacher selection';
    } else if (classFormData.name.length > 30) {
      errors.name = 'Class name cannot exceed 30 characters';
    } else if (/\d/.test(classFormData.name)) {
      errors.name = 'Class name cannot contain numbers';
    }
    
    // Other validations
    if (!classFormData.code.trim()) errors.code = 'Class code is required';
    if (!classFormData.duration) errors.duration = 'Duration is required';
    if (!classFormData.departmentId) errors.departmentId = 'Department is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch departments for the dropdown with error handling and logging
  const { data: departmentsResponse = { data: [] }, isLoading: isDeptLoading, error: deptError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        console.log('Fetching departments...');
        const response = await api.get('/institute/get-departments-names');
        console.log('Departments API Response:', response);
        // The API returns { status, message, data: [...] }
        return response.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
        // Show error to user
        setSnackbar({
          open: true,
          message: 'Failed to load departments',
          severity: 'error'
        });
        return { data: [] };
      }
    },
    // Optional: Add retry logic
    retry: 2,
    refetchOnWindowFocus: false
  });

  // Extract departments array from the API envelope (dropdown); table prefers row.department from list API
  const departments = React.useMemo(() => {
    const env = departmentsResponse;
    if (Array.isArray(env)) return env;
    if (Array.isArray(env?.data)) return env.data;
    if (Array.isArray(env?.data?.data)) return env.data.data;
    return [];
  }, [departmentsResponse]);

  const departmentsForSelect = React.useMemo(() => {
    const base = [...(departments || [])];
    if (editDepartmentFallback?.id != null) {
      const fid = String(editDepartmentFallback.id);
      if (!base.some((d) => d && String(d.id) === fid)) {
        base.unshift({
          id: editDepartmentFallback.id,
          name: editDepartmentFallback.name || `Department ${editDepartmentFallback.id}`,
        });
      }
    }
    return base;
  }, [departments, editDepartmentFallback]);

  // Log any errors
  if (deptError) {
    console.error('Departments query error:', deptError);
  }

  // Fetch classes with pagination, sorting, and search
  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      'classes',
      {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortField: sortModel[0]?.field || 'id',
        sortOrder: sortModel[0]?.sort || 'DESC',
        search: searchQuery,
        departmentId: departmentFilter,
        status: statusFilter,
        courseId: courseFilter,
        instituteId: effectiveInstituteId ?? currentUser?.instituteId ?? selectedInstitute?.id,
      }
    ],
    queryFn: async ({ queryKey }) => {
      const { page, pageSize, sortField, sortOrder, search, departmentId, status, courseId } = queryKey[1];
      // Ensure we have valid page and pageSize values
      const currentPage = Math.max(0, page);
      const currentPageSize = Number(pageSize) || 10; // Ensure it's a number
      
      const params = new URLSearchParams({
        page: currentPage + 1, // API is 1-indexed, MUI is 0-indexed
        limit: currentPageSize,
        sort: sortField,
        order: sortOrder,
        search: search || '',
      });
      // SuperAdmin uses selectedInstitute; institute/hod/teacher use currentUser.instituteId
      const instituteId = effectiveInstituteId ?? currentUser?.instituteId ?? selectedInstitute?.id;
      if (instituteId) params.set('instituteId', instituteId);
      if (departmentId != null && departmentId !== '') {
        params.set('departmentId', departmentId === "all" ? "all" : Number(departmentId));
      }
      if (status && status !== "all") {
        params.set("status", status);
      }
      if (courseId != null && courseId !== "") {
        params.set("courseId", String(courseId));
      }

      console.log('Fetching classes with params:', params.toString());

      try {
        const response = await api.get(`/class/get-all-classes?${params.toString()}`, {
          headers: {
            'accept': '*/*'
          }
        });
        
        console.log('Classes API Response:', response);

        // Normalize various possible response shapes
        const resp = response?.data ?? {};
        const items = (
          Array.isArray(resp?.classes) ? resp.classes :
          Array.isArray(resp?.data?.classes) ? resp.data.classes :
          Array.isArray(resp?.data) ? resp.data :
          Array.isArray(resp?.rows) ? resp.rows :
          Array.isArray(resp?.items) ? resp.items :
          Array.isArray(resp?.result) ? resp.result :
          Array.isArray(resp?.content) ? resp.content :
          Array.isArray(resp?.data?.data) ? resp.data.data :
          Array.isArray(resp?.data?.rows) ? resp.data.rows :
          []
        );

        const totalRaw = (
          resp?.total ??
          resp?.count ??
          resp?.pagination?.total ??
          resp?.pagination?.totalItems ??
          resp?.meta?.totalItems ??
          resp?.data?.pagination?.total ??
          resp?.data?.pagination?.totalItems
        );
        const total = Number(totalRaw ?? (Array.isArray(items) ? items.length : 0)) || 0;

        const pageRaw = (
          resp?.page ??
          resp?.pagination?.page ??
          resp?.pagination?.currentPage ??
          resp?.meta?.currentPage
        );
        const pageValue = Number(pageRaw) || (currentPage + 1);

        const limitRaw = (
          resp?.limit ??
          resp?.pagination?.pageSize ??
          resp?.pagination?.itemsPerPage ??
          resp?.meta?.itemsPerPage
        );
        const limit = Number(limitRaw) || currentPageSize;

        const filterOptions = resp?.filterOptions ?? {};
        return {
          data: {
            classes: items,
            pagination: {
              total,
              page: pageValue,
              pageSize: limit,
              totalPages: Math.max(1, Math.ceil(total / (limit || 1)))
            },
            filterOptions
          }
        };
      } catch (error) {
        console.error('Error fetching classes:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load classes',
          severity: 'error'
        });
        return { data: { classes: [], pagination: { total: 0 } } };
      }
    },
    // Ensure the query runs when the component mounts
    enabled: true,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  // Extract classes and statistics from the response with proper defaults
  const responseData = apiResponse?.data || { classes: [], pagination: { total: 0 }, filterOptions: {} };
  const classesList = Array.isArray(responseData.classes) ? responseData.classes : [];
  const rowCount =
    Number(
      responseData.pagination?.totalItems ??
        responseData.pagination?.total ??
        responseData.pagination?.totalPages * responseData.pagination?.pageSize
    ) || 0;
  // Statistics for the dashboard
  const stats = [
    {
      label: 'Total Classes',
      value: rowCount,
      color: 'primary.main',
    },
    {
      label: 'Active Classes',
      value: classesList.filter(c => c.status === 'active').length,
      color: 'success.main',
    },
    {
      label: 'Total Students',
      value: classesList.reduce((acc, curr) => acc + (curr.studentsCount || 0), 0),
      color: 'info.main',
    },
    {
      label: 'Total Subjects',
      value: classesList.reduce((acc, curr) => acc + (curr.subjectsCount || 0), 0),
      color: 'warning.main',
    },
  ];
  
  // Columns for the data grid
  const columns = [
    { 
      field: 'name', 
      headerName: 'Class Name', 
      flex: 1,
      renderCell: (params) => {
        const className = params.row.name || "-";
        const classCode = params.row.code || "";
        return (
          <Tooltip title={className} placement="top-start" enterDelay={400}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36, flexShrink: 0 }}>
                <School />
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {className}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {classCode}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    { 
      field: 'department', 
      headerName: 'Department', 
      flex: 1,
      valueGetter: (params) => {
        const row = params?.row;
        if (!row) return 'N/A';
        const nestedName = row.department?.name || row.Department?.name;
        if (nestedName) return nestedName;
        const dept = departments.find(
          (d) => d && String(d.id) === String(row.departmentId)
        );
        return dept?.name || 'N/A';
      }
    },
    { 
      field: 'section', 
      headerName: 'Section', 
      width: 120,
      valueFormatter: (params) => params.value || 'N/A'
    },
    {
      field: 'course',
      headerName: 'Courses',
      width: 180,
      valueGetter: (params) => {
        const course = params.row?.Course || params.row?.course;
        return course ? (course.name || course.title || 'N/A') : 'N/A';
      }
    },
    {
      field: 'classTeacher',
      headerName: 'Class Teacher',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => getClassTeacherDisplayName(params.row),
      renderCell: (params) => {
        const name = getClassTeacherDisplayName(params.row);
        const email = getClassTeacherEmail(params.row);
        if (name === "—") {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Box>
            <Typography variant="body2">{name}</Typography>
            {email && name !== email ? (
              <Typography variant="caption" color="text.secondary">
                {email}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'practicalBatches',
      headerName: 'Practical Batches',
      width: 220,
      renderCell: (params) => {
        const list = Array.isArray(params.value) ? params.value : [];
        if (!list.length) return 'None';
        return list.join(', ');
      },
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={params.value ? 'Active' : 'Inactive'} 
            color={params.value ? 'success' : 'default'} 
            size="small" 
            sx={{ minWidth: 80, fontWeight: 'medium' }}
          />
          <Tooltip title={getEntityStatusToggleTooltip(params.row, "class")}>
            <span>
              <Switch
                checked={params.value}
                onChange={() => handleStatusToggle(params.row)}
                color="success"
                size="small"
                disabled={
                  !canToggleEntityStatus(params.row) ||
                  (toggleClassStatus.isLoading && toggleClassStatus.variables?.id === params.row.id)
                }
              />
            </span>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton 
              size="small"
              onClick={() => handleViewClass(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <PermissionGate permission="class.update">
            <Tooltip title="Edit">
              <IconButton 
                size="small"
                onClick={() => handleEditClass(params.row)}
                color="primary"
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="class.delete">
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                color="error"
                onClick={() => handleDeleteClick(params.row)}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
  ];
  
  // Update classes state when data is loaded
  useEffect(() => {
    if (apiResponse?.data?.classes) {
      setClasses(apiResponse.data.classes);
    }
  }, [apiResponse]);

  const importSteps = ["Select File", "Preview & Validate", "Import"];

  const samplePreviewData = [
    {
      name: "Class 1",
      code: "CL1",
      duration: "3 years",
      department: "Computer Science",
      status: "valid",
    },
    {
      name: "Class 2",
      code: "CL2",
      duration: "4 years",
      department: "Computer Science",
      status: "valid",
    },
    {
      name: "Class 3",
      code: "",
      duration: "2 years",
      department: "Computer Science",
      status: "error",
      error: "Class code is required",
    },
    {
      name: "Class 4",
      code: "CL4",
      duration: "1 year",
      department: "Computer Science",
      status: "valid",
    },
  ];

  // Import/Export functions are defined later in the file

  const handleViewImportReport = (report) => {
    setSelectedImportReport(report);
    setImportResultsDialogOpen(true);
  };

  const formatCellForCsv = (val) => {
    if (val == null) return "";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const handleDownloadReport = async (report) => {
    try {
      // If backend provided a direct download URL (common for large/report files), use it.
      if (report?.downloadUrl || report?.fileUrl) {
        const urlToDownload = report.downloadUrl || report.fileUrl;
        const resp = await fetch(urlToDownload);
        if (!resp.ok) throw new Error('Failed to download report file');
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = report.fileName || `class_import_report_${report.id || Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return;
      }

      // Fallback: construct CSV from errors or successes if provided inline
      const errors = Array.isArray(report?.errors) ? report.errors : (Array.isArray(report?.failedRows) ? report.failedRows : []);
      const successes = Array.isArray(report?.successRows) ? report.successRows : (Array.isArray(report?.successes) ? report.successes : []);

      const escapeCsv = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;

      let csvLines = [];
      csvLines.push(`Import Report: ${report?.fileName || `report_${report?.id || ''}`}`);
      csvLines.push(`Date: ${report?.uploadedAt || new Date().toISOString()}`);
      csvLines.push(`Total: ${report?.totalRows ?? report?.total ?? (errors.length + successes.length)} | Success: ${report?.successful ?? successes.length} | Failed: ${report?.failed ?? errors.length}`);
      csvLines.push('');

      if (errors.length > 0) {
        const headers = ["Row", "Field", "Value", "Error"];
        csvLines.push(headers.join(","));
        errors.forEach((e) => {
          csvLines.push([
            escapeCsv(e.row ?? e.rowNumber ?? ''),
            escapeCsv(e.field ?? ''),
            escapeCsv(e.value ?? ''),
            escapeCsv(e.reason || e.error || e.message || '')
          ].join(","));
        });
      } else if (successes.length > 0) {
        // Build CSV from first success object keys if it's an object array
        if (typeof successes[0] === 'object' && successes[0] !== null) {
          const keys = Object.keys(successes[0]);
          csvLines.push(keys.join(","));
          successes.forEach((s) => {
            csvLines.push(keys.map(k => escapeCsv(formatCellForCsv(s[k]))).join(","));
          });
        } else {
          // Simple list
          csvLines.push("Row,Data");
          successes.forEach((s, idx) => {
            csvLines.push([idx + 1, escapeCsv(String(s))].join(","));
          });
        }
      } else {
        // No structured report - create a minimal note
        csvLines.push("Note");
        csvLines.push(escapeCsv("No detailed report available."));
      }

      const csvContent = csvLines.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = report?.fileName || `class_import_report_${report?.id || Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download report:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to download report",
        severity: "error"
      });
    }
  };

  const handleDeleteClass = async () => {
    if (!classToDelete) return;
    
    try {
      // Updated to match the API endpoint and request format from the curl example
      await api.delete('/class/delete-class', { 
        data: { 
          classId: classToDelete.id 
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Invalidate and refetch classes to update the list and reset pagination to first page
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      await queryClient.refetchQueries({ queryKey: ['classes'] });
      setPaginationModel(prev => ({ ...prev, page: 0 }));

      setSnackbar({
        open: true,
        message: 'Class deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting class:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete class',
        severity: 'error'
      });
    } finally {
      setDeleteClassDialogOpen(false);
      setClassToDelete(null);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // TODO: Add file validation and preview logic here
    setSelectedFile(file);
    setImportStep(1);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.post("/institute/download-class-csv-format", {}, { responseType: "json" });
      const blob = new Blob([response.data.csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download template:", err);
      // TODO: Show error message
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post('/institute/import-class-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImportStep(2);
      setSnackbar({
        open: true,
        message: 'Classes imported successfully',
        severity: 'success',
      });
      
      // Refresh the classes list
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      // Immediately refetch and reset to first page so imported rows are visible
      await queryClient.refetchQueries({ queryKey: ['classes'] });
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      
    } catch (error) {
      console.error('Error importing classes:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to import classes',
        severity: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.post('/class/export-csv-classes', {}, {
        responseType: 'blob',
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `classes_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      setSnackbar({
        open: true,
        message: 'Export started successfully',
        severity: 'success',
      });
      
    } catch (error) {
      console.error('Error exporting classes:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to export classes',
        severity: 'error',
      });
    }
  };

  const resetImportDialog = () => {
    setImportStep(0);
    setSelectedFile(null);
    setPreviewData([]);
    setImportDialogOpen(false);
  };

  const handleDeleteDialogClose = () => {
    setDeleteClassDialogOpen(false);
    setClassToDelete(null);
  };

  const resetForm = () => {
    setClassFormData({
      name: "",
      code: "",
      duration: "",
      description: "",
      departmentId: ""
    });
    setIsEditMode(false);
    setEditingClassId(null);
    setViewingClass(null);
  };

  const handleSubmitClass = async (e) => {
    e.preventDefault();
    
    // Validate Add/Edit Class form (name and section constraints)
    const errors = validateAddClassForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.values(errors)[0];
      setSnackbar({
        open: true,
        message: firstError,
        severity: 'error'
      });
      return;
    }
    
    setLoading(prev => ({ ...prev, submitting: true }));

    const parsePracticalBatches = (rawInput) => {
      const raw = String(rawInput || '').trim();
      if (!raw) return { list: [] };

      const tokens = raw
        .split(',')
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);

      if (tokens.length === 0) {
        return { error: 'Please enter valid practical batches (example: A1, A2, A3)' };
      }

      const invalid = tokens.find((item) => !/^[A-Z0-9_-]{1,20}$/.test(item));
      if (invalid) {
        return { error: `Invalid batch code '${invalid}'. Use only letters, numbers, _ or -` };
      }

      const unique = Array.from(new Set(tokens));
      if (unique.length !== tokens.length) {
        return { error: 'Duplicate practical batches are not allowed' };
      }

      return { list: unique };
    };
    
    try {
      // Prepare the request data according to the API specification
      const requestData = {
        name: classForm.name.trim(),
        section: classForm.section.trim(),
        courseId: parseInt(classForm.courseId, 10),
        teacherId: parseInt(classForm.teacherId, 10),
        practicalBatches: [],
        duration: classForm.duration || null,
        description: classForm.description || null,
        departmentId: classForm.departmentId ? parseInt(classForm.departmentId, 10) : null,
        status: classForm.status || false,
        capacity: classForm.capacity ? parseInt(classForm.capacity, 10) : null
      };

      const parsedBatches = parsePracticalBatches(classForm.practicalBatchesInput);
      if (parsedBatches.error) {
        setSnackbar({
          open: true,
          message: parsedBatches.error,
          severity: 'error'
        });
        setLoading(prev => ({ ...prev, submitting: false }));
        return;
      }
      requestData.practicalBatches = parsedBatches.list;
      
      let response;
      
      if (isEditMode && editingClassId) {
        // Update existing class
        console.log('Updating class with ID:', editingClassId, 'Data:', requestData);
        // Update the request data structure to match the API spec
        const updateData = {
          classId: parseInt(editingClassId, 10),
          name: requestData.name,
          section: requestData.section,
          courseId: requestData.courseId,
          teacherId: requestData.teacherId,
          practicalBatches: requestData.practicalBatches,
          duration: requestData.duration,
          description: requestData.description,
          departmentId: requestData.departmentId,
          status: requestData.status,
          capacity: requestData.capacity
        };
        response = await api.put('/class/update-class', updateData, {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*'
          }
        });
      } else {
        // Create new class
        console.log('Creating new class with data:', requestData);
        response = await api.post('/class/create-class', requestData, {
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*'
          }
        });
      }
      
      console.log('API Response:', response);
      
      if (response.data) {
        // Show success message
        setSnackbar({
          open: true,
          message: isEditMode 
            ? (response.data.message || 'Class updated successfully!')
            : (response.data.message || 'Class created successfully!'),
          severity: 'success',
          autoHideDuration: 3000
        });
        
        // Reset form and state
        setClassForm({
          name: '',
          section: '',
          courseId: '',
          teacherId: '',
          practicalBatchesInput: '',
          duration: '',
          description: '',
          departmentId: '',
          status: false
        });
        setTeachers([]);
        setIsEditMode(false);
        setEditingClassId(null);
        setOpenAddClassDialog(false);
        
        // Refresh the classes list
        queryClient.invalidateQueries({ queryKey: ['classes'] });
        queryClient.refetchQueries({ queryKey: ['classes'] });
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      } else {
        throw new Error(response.data?.message || (isEditMode ? 'Failed to update class' : 'Failed to create class'));
      }
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} class:`, error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} class. Please try again.`,
        severity: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  // Columns definition is moved to the top level

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Class Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage classes in your department
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permission="class.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="class.export">
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={classesList.length === 0}
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="class.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddClass}
            >
              Add New Class
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              placeholder="Search classes..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <StatusDropdown
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              label="Status"
              size="small"
              sx={{ minWidth: 160 }}
            />
            <DepartmentModuleFilterSelect
              labelId="classes-department-filter"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              instituteId={effectiveInstituteId}
              showAllOption={showAllDepartmentsOption}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="classes-course-filter-label">Course</InputLabel>
              <Select
                labelId="classes-course-filter-label"
                value={courseFilter}
                label="Course"
                onChange={(e) => {
                  setCourseFilter(e.target.value);
                  setPaginationModel(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">All courses</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name || c.title || `Course ${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <DataGrid
              rows={classesList}
              columns={columns}
              loading={isLoading || isFetching}
              autoHeight
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              paginationMode="server"
              rowCount={rowCount}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              disableRowSelectionOnClick
              sx={{ border: "none" }}
            />
        </CardContent>
      </Card>

      {/* View Class Dialog */}
      <Dialog 
        open={viewClassDialogOpen} 
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewClassDialogOpen(false);
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Class Details</DialogTitle>
        <DialogContent>
          {viewingClass && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Header with Class Name and Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {viewingClass.name || 'Class Name'}
                    {viewingClass.section ? ` - ${viewingClass.section}` : ""}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Basic Information */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Class Name</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingClass.name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Section</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingClass.section || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Class Teacher</Typography>
                  {(() => {
                    const teacherName = getClassTeacherDisplayName(viewingClass);
                    const teacherEmail = getClassTeacherEmail(viewingClass);
                    return (
                      <>
                        <Typography variant="body1" gutterBottom>
                          {teacherName === "—" ? "N/A" : teacherName}
                        </Typography>
                        {teacherEmail ? (
                          <Typography variant="caption" color="text.secondary">
                            {teacherEmail}
                          </Typography>
                        ) : null}
                      </>
                    );
                  })()}
                </Grid>

                {/* Course Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Course Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Course Name</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingClass.Course?.name || viewingClass.course?.name || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Course Code</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingClass.Course?.code || viewingClass.course?.code || 'N/A'}
                  </Typography>
                </Grid>


                {/* Department Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Department Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Department Name</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingClass.Department?.name || viewingClass.department?.name || 'N/A'}
                  </Typography>
                </Grid>


                {/* Status and Additional Info */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={viewingClass.status ? 'Active' : 'Inactive'} 
                        color={viewingClass.status ? 'success' : 'default'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {viewingClass.createdAt ? displayDate(viewingClass.createdAt, "N/A") : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => setViewClassDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Classes Dialog */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="classes"
        instituteId={currentUser?.instituteId ?? selectedInstitute?.id}
        onImportComplete={async () => {
          // Ensure the classes query is invalidated, refetched and pagination set to first page
          queryClient.invalidateQueries({ queryKey: ['classes'] });
          await queryClient.refetchQueries({ queryKey: ['classes'] });
          setPaginationModel(prev => ({ ...prev, page: 0 }));
        }}
      />

      {/* Add/Edit Class Dialog */}
      <Dialog 
        open={openAddClassDialog} 
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseAddClass();
        }}
        maxWidth="sm" 
        fullWidth
      >
        <form onSubmit={handleSubmitClass}>
          <DialogTitle>{isEditMode ? 'Update Class' : 'Create Class'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Class Name"
                  name="name"
                  value={classForm.name}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  inputProps={{
                    minLength: 2,
                    maxLength: 30,
                    'data-testid': 'class-name-input'
                  }}
                  error={!!formErrors.name}
                  helperText={formErrors.name || '2-30 characters'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Section"
                  name="section"
                  value={classForm.section}
                  onChange={handleSectionChange}
                  onKeyDown={blockNumberKeyDown}
                  required
                  margin="normal"
                  inputProps={{
                    minLength: 1,
                    maxLength: 1,
                    'data-testid': 'class-section-input'
                  }}
                  error={!!formErrors.section}
                  helperText={formErrors.section || 'Exactly 1 character'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Practical Batches"
                  name="practicalBatchesInput"
                  value={classForm.practicalBatchesInput || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  placeholder="A1, A2, A3"
                  helperText="Comma-separated batch codes. Leave empty if class has no batch split."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required error={!!formErrors.department}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="departmentId"
                    value={classForm.departmentId != null && classForm.departmentId !== '' ? String(classForm.departmentId) : ''}
                    onChange={handleInputChange}
                    label="Department"
                    disabled={isDeptLoading}
                  >
                    {isDeptLoading ? (
                      <MenuItem disabled>Loading departments...</MenuItem>
                    ) : Array.isArray(departmentsForSelect) && departmentsForSelect.length > 0 ? (
                      departmentsForSelect.map((dept) => (
                        <MenuItem key={String(dept.id)} value={String(dept.id)}>
                          {dept.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No departments available</MenuItem>
                    )}
                  </Select>
                  {!!formErrors.department && (
                    <FormHelperText>{formErrors.department}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required error={isCoursesError}>
                  <InputLabel>Course</InputLabel>
                  <Select
                    name="courseId"
                    value={classForm.courseId != null && classForm.courseId !== '' ? String(classForm.courseId) : ''}
                    onChange={handleCourseChange}
                    label="Course"
                    disabled={isLoadingCourses}
                    startAdornment={
                      isLoadingCourses && (
                        <InputAdornment position="start">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      )
                    }
                  >
                    {isLoadingCourses ? (
                      <MenuItem disabled>Loading courses...</MenuItem>
                    ) : isCoursesError ? (
                      <MenuItem disabled>Error loading courses</MenuItem>
                    ) : Array.isArray(coursesForSelect) && coursesForSelect.length > 0 ? (
                      coursesForSelect.map((course) => {
                        const cid = course?.id ?? course?._id ?? "";
                        const courseName = course?.name || course?.courseName || `Course ${cid}`;
                        return (
                          <MenuItem key={String(cid)} value={String(cid)}>
                            {courseName}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled>No courses available</MenuItem>
                    )}
                  </Select>
                  {isCoursesError && (
                    <FormHelperText>Error loading courses. Please try again.</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required error={isCoursesError}>
                  <InputLabel>Class Teacher</InputLabel>
                  <Select
                    name="teacherId"
                    value={classForm.teacherId != null && classForm.teacherId !== '' ? String(classForm.teacherId) : ''}
                    onChange={handleInputChange}
                    label="Class Teacher"
                    disabled={!classForm.courseId || loading.teachers}
                    startAdornment={
                      loading.teachers && (
                        <InputAdornment position="start">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      )
                    }
                  >
                    {!classForm.courseId ? (
                      <MenuItem disabled>Select a course first</MenuItem>
                    ) : loading.teachers ? (
                      <MenuItem disabled>Loading teachers...</MenuItem>
                    ) : teachers.length > 0 ? (
                      teachers.map((teacher) => (
                        <MenuItem key={String(teacher.id)} value={String(teacher.id)}>
                          {teacher.fullName || teacher.email || `Teacher ${teacher.id}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No teachers available for this course</MenuItem>
                    )}
                  </Select>
                  {loading.teachers && (
                    <LinearProgress sx={{ mt: 1 }} />
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Capacity"
                  name="capacity"
                  type="number"
                  value={classForm.capacity}
                  onChange={handleInputChange}
                  margin="normal"
                  inputProps={{
                    min: 1,
                    max: 500,
                    'data-testid': 'class-capacity-input'
                  }}
                  helperText="Maximum student capacity for this class"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddClass} color="inherit">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading.submitting}
              startIcon={loading.submitting ? <CircularProgress size={20} /> : (isEditMode ? <Edit /> : <Add />)}
            >
              {loading.submitting ? 'Saving...' : (isEditMode ? 'Update Class' : 'Create Class')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteClassDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDeleteClassDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the class "{classToDelete?.name || ''}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteClassDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteClass}
            color="error"
            variant="contained"
            startIcon={<Delete />}
            disabled={toggleClassStatus.isLoading}
          >
            {toggleClassStatus.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Classes;
